import { formatTomato } from '@tomato/shared';
import { useState } from 'react';

import { ApiError } from '../api/client';
import { InvitePanel } from '../components/InvitePanel';
import { Button, CenterCard, Logo, Notice } from '../components/ui';
import { isMobile } from '../lib/browserEnv';
import { isUserCancel, passkeySupported, registerPasskey } from '../lib/passkey';
import { useSessionStore } from '../store/session';

/**
 * 가입 직후 1회 넛지(C). 재방문·전파를 쉽게 만드는 세 가지를 한 화면에 모은다:
 * ① 패스키 등록(다음부턴 인증번호 없이) ② 홈 화면에 추가 ③ 친구 초대(100토마토).
 * 강요하지 않는다 — "둘러보기"로 언제든 건너뛴다.
 */
export function WelcomeScreen() {
  const me = useSessionStore((s) => s.me);
  const dismiss = useSessionStore((s) => s.dismissWelcome);
  const [passkeyDone, setPasskeyDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function addPasskey() {
    setBusy(true);
    setError('');
    try {
      await registerPasskey();
      setPasskeyDone(true);
    } catch (err) {
      if (isUserCancel(err)) return;
      setError(err instanceof ApiError ? err.message : '패스키를 등록하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <CenterCard>
      <Logo sub={`환영해요, ${me?.nickname}님!`} />

      <p className="text-center text-[13px] leading-relaxed text-ink-500">
        가입 축하 <b className="text-tomato-600">{formatTomato(me?.tomatoBalance ?? 0)}</b> 적립됐어요.
        <br />
        방문·댓글·글쓰기마다 토마토가 쌓여요.
      </p>

      {/* ① 패스키 */}
      {passkeySupported() && (
        <div className="mt-6 rounded-xl bg-ink-50 p-3.5">
          <p className="text-[13px] font-medium text-ink-800">🔑 다음부턴 더 빠르게</p>
          <p className="mt-1 text-[12px] text-ink-500">
            이 기기에 패스키를 등록하면 인증번호 없이 지문·Face ID로 들어와요.
            {isMobile() && ' 회사 PC에서도 QR 스캔 한 번으로 로그인돼요.'}
          </p>
          {passkeyDone ? (
            <p className="mt-2 text-[12px] text-tomato-600">등록됐어요 ✓</p>
          ) : (
            <Button size="sm" className="mt-2.5" disabled={busy} onClick={() => void addPasskey()}>
              {busy ? '등록 중…' : '패스키 등록'}
            </Button>
          )}
        </div>
      )}

      {/* ② 홈 화면 추가 안내 */}
      <div className="mt-3 rounded-xl bg-ink-50 p-3.5">
        <p className="text-[13px] font-medium text-ink-800">📱 홈 화면에 추가</p>
        <p className="mt-1 text-[12px] leading-relaxed text-ink-500">
          브라우저 공유·메뉴에서 <b>홈 화면에 추가</b>하면 앱처럼 한 번에 열려요. (설정에서 다시 안내해요)
        </p>
      </div>

      {/* ③ 친구 초대 */}
      <div className="mt-3 rounded-xl bg-ink-50 p-3.5">
        <InvitePanel bare />
      </div>

      <Notice>{error}</Notice>

      <Button variant="secondary" className="mt-5 w-full" onClick={dismiss}>
        둘러보기
      </Button>
    </CenterCard>
  );
}
